import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSquarePlus, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useFeedbacks } from "@/hooks/useFeedbacks";

const feedbackSchema = z.object({
  type: z.enum(["sugestao", "problema", "elogio", "outro"], {
    required_error: "Selecione um tipo de feedback",
  }),
  subject: z
    .string()
    .min(5, "O assunto deve ter pelo menos 5 caracteres")
    .max(100, "O assunto deve ter no máximo 100 caracteres"),
  message: z
    .string()
    .min(20, "A mensagem deve ter pelo menos 20 caracteres")
    .max(1000, "A mensagem deve ter no máximo 1000 caracteres"),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface FeedbackFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const typeLabels: Record<string, string> = {
  sugestao: "Sugestão",
  problema: "Problema",
  elogio: "Elogio",
  outro: "Outro",
};

export function FeedbackForm({ open, onOpenChange }: FeedbackFormProps) {
  const { user } = useAuth();
  const { submitFeedback, submitting } = useFeedbacks();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      type: "sugestao",
      subject: "",
      message: "",
    },
  });

  async function onSubmit(data: FeedbackFormData) {
    if (!user) return;

    const result = await submitFeedback({
      user_id: user.id,
      user_name: user.user_metadata?.name || user.email?.split("@")[0] || "Usuário",
      user_email: user.email || "",
      type: data.type,
      subject: data.subject,
      message: data.message,
    });

    if (result.success) {
      setSubmitted(true);
      form.reset();
      setTimeout(() => {
        onOpenChange(false);
        setSubmitted(false);
      }, 2000);
    }
  }

  function handleClose() {
    form.reset();
    setSubmitted(false);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <MessageSquarePlus className="h-6 w-6 text-primary" />
            Enviar Feedback
          </DialogTitle>
          <DialogDescription className="text-base">
            Sua opinião é muito importante para nós! Compartilhe sugestões, problemas ou elogios.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <MessageSquarePlus className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Obrigado!</h3>
            <p className="mt-2 text-muted-foreground">
              Seu feedback foi enviado com sucesso.
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">
                      Tipo de Feedback
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 text-base">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(typeLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value} className="text-base">
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">Assunto</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Título curto do seu feedback"
                        className="h-12 text-base"
                        maxLength={100}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">Mensagem</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Descreva com detalhes sua sugestão, problema ou elogio..."
                        className="min-h-32 resize-none text-base"
                        maxLength={1000}
                      />
                    </FormControl>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <FormMessage />
                      <span>{field.value.length}/1000</span>
                    </div>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="h-12 w-full text-base"
                disabled={submitting}
              >
                {submitting ? (
                  "Enviando..."
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Enviar Feedback
                  </>
                )}
              </Button>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
